import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {GaugeCardComponent} from '@/app/shared/components/gauge-card/gauge-card.component';
import {Risk} from '@/app/utils/risk-colors';
import {CampaignStoreService} from '../../../campaña/services/campaign-store.service';
import {AnalysisService} from '../../services/analysis.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {map, of, switchMap} from 'rxjs';
import {CAT_MAP} from '../../mappers/reporte-preview.mappers';
import {OverviewDemographicsService} from '../../services/overview-demographics.service';
import {ThemeService} from "@/app/core/services/theme/theme.service";
import {EmptyStateComponent} from "@/app/shared/components/empty-state/empty-state.component";

/** Convierte un nivel de riesgo a un porcentaje para el gauge */
function percentFromRating(risk: Risk): number {
  const riskValueMap: Record<Risk, number> = {
    'Nulo o despreciable': 10,
    'Muy bajo': 20,
    'Bajo': 40,
    'Medio': 60,
    'Alto': 80,
    'Muy alto': 100,
  };
  return riskValueMap[risk] ?? 0;
}

type GaugeVM = {
  title: string;
  percent: number;
  scoreLabel: string;
  level: Risk;
};

@Component({
  selector: 'app-reporte-preview',
  standalone: true,
  imports: [CommonModule, NgxChartsModule, GaugeCardComponent, EmptyStateComponent],
  templateUrl: './reporte-preview.component.html',
  styleUrls: ['./reporte-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportePreviewComponent {
  positiveCards: GaugeVM[] = [];
  negativeCards: GaugeVM[] = [];
  protected overview = inject(OverviewDemographicsService);
  private store = inject(CampaignStoreService);
  private analysis = inject(AnalysisService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);

  protected themeContent = this.themeService.themeContent;
  protected reportContent = computed(() => this.themeContent()?.reports);
  protected previewContent = computed(() => this.reportContent()?.reportPreviewComponent);

  trackByGauge = (i: number, c: { title?: string }) => `${c?.title ?? 'gauge'}#${i}`;

  ngOnInit(): void {
    // Carga de distribución general al cambiar campaña
    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(camp => {
        if (camp) this.overview.load(String(camp.value), 'guia-iii');
      });

    //  Gauges por categoría
    this.store.selectedCampaign$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(camp => {
          if (!camp) return of([]);
          return this.analysis.getCategoryScores({
            campaign: camp.value,
            guideId: 'guia-iii'
          });
        }),
        map(rows => this.mapCategoryScoresToVM(rows ?? []))
      )
      .subscribe(({positive, negative}) => {
        console.log('[gauges]', {positive, negative});
        this.positiveCards = positive;
        this.negativeCards = negative;
        this.cdr.markForCheck();
      });
  }

  /** Normaliza variaciones del backend a nuestro Risk */
  private normalizeRisk(s?: string | null): Risk | 'Sin datos' {
    const v = (s ?? '').trim().toLowerCase();
    if (v === 'muy bajo' || v === 'muy_bajo' || v === 'nulo') return 'Muy bajo' as Risk;
    if (v === 'bajo') return 'Bajo' as Risk;
    if (v === 'medio') return 'Medio' as Risk;
    if (v === 'alto') return 'Alto' as Risk;
    if (v === 'muy alto' || v === 'muy_alto') return 'Muy alto' as Risk;
    return 'Sin datos';
  }

  /** Reglas: Muy bajo/Bajo → positivos; Medio/Alto/Muy alto → negativos; Sin datos → omitir */
  private bucketFor(level: Risk | 'Sin datos'): 'positive' | 'negative' | 'skip' {
    if (level === 'Muy bajo' || level === 'Bajo') return 'positive';
    if (level === 'Medio' || level === 'Alto' || level === 'Muy alto') return 'negative';
    return 'skip';
  }

  private mapCategoryScoresToVM(
    rows: Array<{ categoryName: string; averageScore: number; rating?: string; totalRespondents?: number }>
  ) {
    // agrupar por categoría
    const byCat = new Map<string, Array<typeof rows[number]>>();
    for (const r of rows ?? []) {
      const key = (r.categoryName || '').trim().toLowerCase();
      if (!key) continue;
      const arr = byCat.get(key) ?? [];
      arr.push(r);
      byCat.set(key, arr);
    }

    // elegir un “representante” por categoría
    const pickRepresentative = (list: Array<typeof rows[number]>) => {
      // prioriza el mayor totalRespondents; si empata, severidad de rating
      const severity = (rt?: string | null) => {
        const v = (rt ?? '').trim().toLowerCase();
        // de menos a más severo
        if (v === 'nulo o despreciable' || v === 'nulo' || v === 'muy_bajo' || v === 'muy bajo') return 0;
        if (v === 'bajo') return 1;
        if (v === 'medio') return 2;
        if (v === 'alto') return 3;
        if (v === 'muy alto' || v === 'muy_alto') return 4;
        return 2; // desconocido → medio
      };

      return [...list].sort((a, b) => {
        const ra = Number(a.totalRespondents ?? 0);
        const rb = Number(b.totalRespondents ?? 0);
        if (rb !== ra) return rb - ra;
        return severity(b.rating) - severity(a.rating);
      })[0];
    };

    const positive: GaugeVM[] = [];
    const negative: GaugeVM[] = [];

    for (const [rawKey, list] of byCat.entries()) {
      const rep = pickRepresentative(list);

      // título desde mapa si existe
      const entry = (CAT_MAP as any)[rawKey] as (typeof CAT_MAP)[keyof typeof CAT_MAP] | undefined;
      const title = entry?.title ?? (rep.categoryName || '').trim();
      const level = this.normalizeRisk(rep.rating) as Risk | 'Sin datos';
      const bucket = this.bucketFor(level);
      if (bucket === 'skip') continue;

      const vm: GaugeVM = {
        title,
        scoreLabel: Number(rep.averageScore ?? 0).toFixed(2),
        level: level as Risk,
        percent: percentFromRating(level as Risk),
      };

      (bucket === 'positive' ? positive : negative).push(vm);
    }

    const orderPos = ['ambiente', 'liderazgo', 'entorno'];
    const orderNeg = ['tiempo', 'familia', 'actividad'];

    const sortByTitleTokens = (list: GaugeVM[], tokens: string[]) =>
      list.sort((a, b) => {
        const i = tokens.findIndex(tok => a.title.toLowerCase().includes(tok));
        const j = tokens.findIndex(tok => b.title.toLowerCase().includes(tok));
        return (i === -1 ? 99 : i) - (j === -1 ? 99 : j);
      });

    return {
      positive: sortByTitleTokens(positive, orderPos),
      negative: sortByTitleTokens(negative, orderNeg),
    };
  }
}
